import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { faCircle, faPause } from '@fortawesome/free-solid-svg-icons';
import { Topic, TopicStatus, TopicType } from '../quizzes.type';
import Button from 'components/base/Button';
import { Dispatch, ReactNode, SetStateAction } from 'react';

const textMapping: Record<TopicType, string> = {
  critical: 'danger',
  supplementary: 'warning'
};

const QuizzesTopicCard = ({
  topic,
  onSelect
}: {
  topic: Topic;
  onSelect: Dispatch<SetStateAction<Topic | null>>;
}) => {
  const actionMapping: Record<TopicStatus, ReactNode> = {
    'in-progress': (
      <div className="d-flex align-items-center gap-3">
        <Button
          variant="phoenix-primary"
          title="The questions have not been prepared yet."
          onClick={() => onSelect(topic)}
        >
          Continue Test
        </Button>
        <span className="text-primary">
          <FontAwesomeIcon icon={faPause} className="me-2" />
          Paused
        </span>
      </div>
    ),
    blocked: <div className="text-danger py-2.5">Blocked</div>,
    qualified: <div className="text-success py-2.5">Qualified</div>
  };

  const statusMapping: Record<TopicStatus, ReactNode> = {
    'in-progress': (
      <div className="d-flex align-items-center h-100">
        <FeatherIcon icon="clock" className="me-2" width={16} height={16} />
        <p className="mb-0 fs-9 fw-semibold text-body-tertiary">
          {topic.duration} Minutes
        </p>
      </div>
    ),
    blocked: (
      <OverlayTrigger
        overlay={
          <Tooltip>
            Your exam attempt was unsuccessful, resulting in a blocked status.
            You may retry after some time.
          </Tooltip>
        }
      >
        <div className="d-flex align-items-center h-100">
          <FeatherIcon
            icon="alert-circle"
            className="me-2"
            width={16}
            height={16}
          />
          <p className="mb-0 fs-9 fw-semibold text-body-tertiary">
            Retry Later
          </p>
        </div>
      </OverlayTrigger>
    ),
    qualified: (
      <OverlayTrigger
        overlay={
          <Tooltip>
            Congratulations! You have successfully passed the exam and are now
            qualified.
          </Tooltip>
        }
      >
        <div className="d-flex align-items-center h-100">
          <FeatherIcon
            icon="check-circle"
            className="me-2"
            width={16}
            height={16}
          />
          <p className="mb-0 fs-9 fw-semibold text-body-tertiary">
            Exam Passed
          </p>
        </div>
      </OverlayTrigger>
    )
  };

  return (
    <Card>
      <Card.Body>
        <div className="border-bottom border-translucent">
          <div className="d-sm-flex align-items-center mb-1">
            <div className="fw-bold fs-7 lh-sm line-clamp-1 me-sm-4">
              {topic.skill.name}
            </div>
            <div className="d-flex align-items-center">
              <FontAwesomeIcon
                icon={faCircle}
                transform="shrink-6 up-1"
                className={`me-1 text-${textMapping[topic.type]}`}
              />
              <span className="fw-bold fs-9 text-body lh-2 text-capitalize">
                {topic.type}
              </span>
            </div>
          </div>
          <p className="fs-9 fw-semibold text-body text mb-4">
            {topic.skill.desc}
          </p>
        </div>
        <Row className="g-1 g-sm-3 mt-2 lh-1 justify-content-between">
          <Col sm="auto">
            {topic.status ? (
              actionMapping[topic.status]
            ) : (
              <Button
                variant="phoenix-primary"
                title="The questions have not been prepared yet."
                onClick={() => onSelect(topic)}
              >
                Start Test
              </Button>
            )}
          </Col>
          {topic.status && <Col sm="auto">{statusMapping[topic.status]}</Col>}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default QuizzesTopicCard;
